import React, { memo, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { VehicleDetail } from "./Vehicle";
import { Vehicle } from "./Vehicle";

import { useWebsocketLoader } from "Pages/AuthorizedApp/WebsocketLoaderController";
import { getOperatorStations } from "Slices/operatorStation";
import {
  fetchGetStationVehiclesFromCurrentSemanticMap,
  getIsMissionsInQueueLoaded,
  getIsMissionsInQueueLoadingError,
  getIsStationVehiclesLoaded,
  getIsStationVehiclesLoadingError,
  getMissionsInQueue,
  getStationVehicles,
} from "Slices/stationConfirmation";
import { getIsStopsByCurrentSemanticMapEmpty } from "Slices/stops";
import { getVehicles } from "Slices/vehicles";
import { getVehicleTypes } from "Slices/vehicleTypes";
import { getVehicleInfoById } from "Utils";
import { getCurrentSemanticMapId } from "Slices/appState";

import { MissionPlansLoadingError } from "Components/DeployedMissionList/components/MissionPlansLoadingError";

import { ReactComponent as VehicleEmptyStateIcon } from "../assets/vehicle-empty-state-icon.svg";
import { ReactComponent as ReadyForDispatchIcon } from "../assets/ready-for-dispatch-icon.svg";
import {
  EmptyStateStyled,
  StationVehiclesStyled,
  SubtitleStyled,
  VehicleListScrollArea,
} from "./styles";
import { LastUpdatedFooter } from "../../common/LastUpdatedFooter";

export const StationInspection = memo(() => {
  const stationVehicles = useSelector(getStationVehicles);
  const stations = useSelector(getOperatorStations);
  const missionsInQueue = useSelector(getMissionsInQueue);
  // Data loading indicators
  const { isWSConnecting } = useWebsocketLoader();
  const isMissionsInQueueLoaded = useSelector(getIsMissionsInQueueLoaded);
  const isStationVehiclesLoaded = useSelector(getIsStationVehiclesLoaded);
  // Hide the loading spinner if the websocket is connected,
  // the missions in queue and the station vehicles are loaded
  const isDataLoaded =
    isMissionsInQueueLoaded && isStationVehiclesLoaded && !isWSConnecting;
  // Error indicators
  const hasMissionsInQueueError = useSelector(getIsMissionsInQueueLoadingError);
  const hasStationVehiclesError = useSelector(getIsStationVehiclesLoadingError);
  const hasDataLoadingError =
    hasMissionsInQueueError || hasStationVehiclesError;
  const isStopsEmpty = useSelector(getIsStopsByCurrentSemanticMapEmpty);
  const vehicleTypes: VehicleType[] = useSelector(getVehicleTypes);
  const vehicles: Array<Vehicle> = useSelector(getVehicles);
  const dispatch = useDispatch();
  const semanticMapId = useSelector(getCurrentSemanticMapId);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(Date.now());

  const noStationsAvailable = useMemo(() => {
    return isStopsEmpty;
  }, [isStopsEmpty]);

  const selectedStationId = useMemo(() => {
    const station = stations.find((each) => each.isPicked);
    return station?.id;
  }, [stations]);

  const vehicleList: VehicleDetail[] = useMemo(() => {
    if (
      selectedStationId &&
      vehicles &&
      vehicleTypes &&
      stationVehicles.length > 0
    ) {
      return stationVehicles
        .filter(
          (each) =>
            each.stopId === selectedStationId &&
            !each.completed &&
            each.completed !== null
        )
        .sort((a, b) => {
          // Sort the list by updatedAt
          const timeUpdateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const timeUpdateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return timeUpdateA - timeUpdateB;
        })
        .map((each) => {
          const { vehicleId, queuedMissionId } = each;
          const { name, link } = getVehicleInfoById(
            vehicleId,
            vehicles,
            vehicleTypes
          );

          return {
            name,
            link,
            missionPlan: queuedMissionId
              ? missionsInQueue.find((item) => item.id === queuedMissionId)
              : undefined,
            ...each,
          };
        });
    }
    return [];
  }, [
    vehicles,
    vehicleTypes,
    missionsInQueue,
    stationVehicles,
    selectedStationId,
  ]);

  const handleRefresh = async () => {
    if (semanticMapId) {
      await dispatch(
        fetchGetStationVehiclesFromCurrentSemanticMap({ semanticMapId })
      );
      setLastUpdatedAt(Date.now());
    }
  };

  return (
    <>
      {!isDataLoaded ? (
        <EmptyStateStyled>
          <VehicleEmptyStateIcon />
          <h3>Fetching data...</h3>
        </EmptyStateStyled>
      ) : hasDataLoadingError ? (
        <MissionPlansLoadingError />
      ) : (
        <StationVehiclesStyled>
          <SubtitleStyled>
            <ReadyForDispatchIcon />
            Vehicles at your station:
          </SubtitleStyled>
          <VehicleListScrollArea>
            {!noStationsAvailable &&
            selectedStationId &&
            vehicleList.length > 0 ? (
              <>
                {vehicleList.map((each, index, arr) => {
                  const { vehicleId } = each;
                  return (
                    <Vehicle
                      key={vehicleId}
                      data={each}
                      isLast={index === arr.length - 1}
                    />
                  );
                })}
              </>
            ) : (
              <EmptyStateStyled>
                <VehicleEmptyStateIcon />
                <h3>No vehicles at your station</h3>
              </EmptyStateStyled>
            )}
          </VehicleListScrollArea>
          <LastUpdatedFooter
            onRefresh={handleRefresh}
            lastUpdatedAt={lastUpdatedAt}
          />
        </StationVehiclesStyled>
      )}
    </>
  );
});
